<template>
  <div>
    <MultiSelectComponent
      v-model="modelValue"
      :options="options"
      :buttonId
      :placeholder="$t('forwards.pools.members.placeholder')"
      :disabled="disabled || locked"
      :maxDisplayLabels="2"
      selectedFirst
      portal
    >
      <template #option="{ option }">
        <div class="fw-medium text-wrap">
          <span
            v-if="countryOf(option.value)"
            class="me-1"
            :title="translateCountryCode(countryOf(option.value)!, t, te)"
            >{{ getFlagEmoji(countryOf(option.value)!) }}</span
          >{{ option.label }}
        </div>
        <div v-if="option.description" class="text-muted small">
          {{ option.description }}
        </div>
      </template>
      <template #empty>{{
        $t("forwards.pools.members.no_candidates")
      }}</template>
    </MultiSelectComponent>

    <p
      v-if="locked"
      class="text-body-tertiary fs-10 mt-2 mb-0"
      data-testid="members-manage-account"
    >
      {{ $t("forwards.pools.members.manage_account") }}
    </p>
    <p
      v-else-if="ownCompaniesOnly"
      class="text-body-tertiary fs-10 mt-2 mb-0"
      data-testid="members-own-companies-only"
    >
      {{ $t("forwards.pools.members.own_companies_only") }}
    </p>

    <p v-if="modelValue.length === 0 && touched" class="error-note mt-3 mb-0">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ $t("forwards.pools.members.at_least_one") }}</span>
    </p>

    <p v-for="blocker in blockers" :key="blocker" class="error-note mt-2 mb-0">
      <FontAwesomeIcon icon="fa-solid fa-exclamation-triangle" />
      <span>{{ blocker }}</span>
    </p>
  </div>
</template>

<script setup lang="ts">
import MultiSelectComponent from "@/components/common/MultiSelectComponent.vue";
import type { PoolMemberRow } from "@/forwards2/forwardsTypes";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { keyBy, sortBy } from "lodash-es";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Which companies sell through this pool: the standard company dropdown, closed on the first names and
 * the count.
 *
 * A company without `forward_sales_enabled` stays *visible and disabled* with the reason next to it
 * (D-09a-01): leaving it out of the list is exactly the confusion the requirement exists to avoid.
 * The flag gates attaching only, so a member whose flag was revoked after it joined stays removable —
 * as does every current member: removing one must never be what leaves a pool unsavable.
 *
 * A pool's companies change only as an account that owns them (user, 2026-10-20). `locked` is a
 * super admin acting as themselves on a client's pool, who edits the rest and is told to manage an
 * account; `ownCompaniesOnly` the same admin starting a pool, offered their own account's companies.
 *
 * Removing a member can be refused by the backend when the pool would no longer cover what it owes;
 * `blockers` carries that explanation from the preview, unchanged.
 */
const props = withDefaults(
  defineProps<{
    /** Every company the actor could attach, with the two flags that decide whether it may. */
    candidates: PoolMemberRow[];
    /** Save-blocking reasons from `POST /pools/preview`, rendered verbatim. */
    blockers?: string[];
    disabled?: boolean;
    locked?: boolean;
    ownCompaniesOnly?: boolean;
  }>(),
  {
    blockers: () => [],
    disabled: false,
    locked: false,
    ownCompaniesOnly: false,
  }
);

const modelValue = defineModel<number[]>({ required: true });

const { t, te } = useI18n();
const buttonId = useId();
// A new pool opens with no company yet, which is the user's to fill in, not a mistake to flag; the
// missing-company note waits until a selection has been made and emptied again.
const touched = ref(modelValue.value.length > 0);

const candidatesById = computed(() =>
  keyBy(props.candidates, (candidate) => candidate.company_id)
);

const options = computed(() =>
  sortBy(props.candidates, [
    (candidate) => !candidate.forward_sales_enabled,
    (candidate) => candidate.company_name.toLowerCase(),
  ]).map((candidate) => ({
    label: candidate.company_name,
    value: candidate.company_id,
    disabled: isBlocked(candidate),
    description: isBlocked(candidate)
      ? t("forwards.pools.members.not_enabled")
      : candidate.allocation_ready
        ? undefined
        : t("forwards.pools.members.not_allocation_ready"),
  }))
);

watch(
  () => modelValue.value.length,
  (count) => {
    if (count > 0) touched.value = true;
  }
);

const countryOf = (companyId: number): string | null =>
  candidatesById.value[companyId]?.country ?? null;

/** Whether this company cannot be attached; a current member never is (RV2-pool-lifecycle-04). */
const isBlocked = (candidate: PoolMemberRow) =>
  !candidate.forward_sales_enabled &&
  !modelValue.value.includes(candidate.company_id);
</script>
